import React, { useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import { Button, Chip, Alert, Paper, TextField } from '@mui/material'
import { toast } from 'react-toastify'
import { importVtex, getVtex, getVtexPreview, getVtexEstado, pushVtex, getApiBonos } from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`

const ESTADO = {
  activo: { color: 'success', txt: 'Activo en Vtex' },
  inactivo: { color: 'warning', txt: 'Inactivo en Vtex' },
  sin_ficha: { color: 'error', txt: 'Sin ficha' },
  huerfano: { color: 'default', txt: 'Huérfano (sin material)' },
}
const chipFicha = (e) => {
  const s = ESTADO[e] || { color: 'default', txt: e }
  return <Chip size="small" color={s.color} label={s.txt} variant="outlined" />
}

// Columnas del export de Vtex (la fila de encabezado real es la primera de datos).
const COL = { material: '__EMPTY_28', skuId: '__EMPTY_23', prodId: '__EMPTY', skuName: '__EMPTY_24', brand: '__EMPTY_6', active: '__EMPTY_26' }
const s = (v) => String(v == null ? '' : v).trim()

const VtexTab = ({ onChanged }) => {
  const [mapeos, setMapeos] = useState([])
  const [preview, setPreview] = useState([])
  const [estado, setEstado] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [apiMaterial, setApiMaterial] = useState('')
  const [apiSku, setApiSku] = useState('')
  const [apiRespuesta, setApiRespuesta] = useState('')
  const [apiError, setApiError] = useState('')

  const fetchData = async () => {
    setIsLoading(true)
    const [m, p, e] = await Promise.all([getVtex(), getVtexPreview(), getVtexEstado()])
    if (m && m.success) setMapeos(m.data)
    if (p && p.success) setPreview(p.data)
    if (e && e.success) setEstado(e.data)
    setIsLoading(false)
  }

  const cuenta = (est) => estado.filter((x) => x.estado === est).length

  useEffect(() => {
    fetchData()
  }, [])

  const onArchivo = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      try {
        const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' })
        const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })
        const items = raw
          .slice(1) // primera fila = encabezado real
          .map((r) => ({
            material: s(r[COL.material]),
            sku_id: s(r[COL.skuId]),
            prod_id: s(r[COL.prodId]),
            sku_name: s(r[COL.skuName]),
            brand: s(r[COL.brand]),
            active: s(r[COL.active]) === 'Yes' ? '1' : '0',
          }))
          .filter((it) => it.material && ['victory', 'tvs'].includes(it.brand.toLowerCase()))
        if (!items.length) {
          toast.error('No se encontraron productos Victory/TVS en el archivo.')
          return
        }
        const res = await importVtex(items)
        if (res && res.success) {
          toast.success(`${res.importados} SKU importados · ${res.emparejados} emparejados con materiales.`)
          fetchData()
        } else {
          toast.error('No se pudo importar.')
        }
      } catch (err) {
        toast.error('Archivo no válido.')
      }
    }
    reader.readAsArrayBuffer(file)
    e.target.value = ''
  }

  const simularEnvio = async () => {
    const res = await pushVtex()
    if (res && res.success && res.ok) {
      toast.success(`${res.procesadas} SKU procesados (SIMULADO — no se envió nada a Vtex real).`)
      fetchData()
      onChanged?.()
    } else {
      toast.info((res && res.mensaje) || 'Sin cambios que enviar.')
    }
  }

  // Prueba del endpoint que consume Vtex. Se llama con la sesión del usuario: la api key nunca llega al navegador.
  const probarApi = async () => {
    if (!apiMaterial.trim() && !apiSku.trim()) {
      toast.info('Indica material o SKU de Vtex.')
      return
    }
    setApiRespuesta('')
    setApiError('')
    const res = await getApiBonos(apiMaterial.trim(), apiSku.trim())
    if (res) setApiRespuesta(JSON.stringify(res, null, 2))
    else setApiError('La consulta falló o no fue autorizada.')
  }

  const emparejados = mapeos.filter((m) => String(m.emparejado) === '1').length

  const cols = useMemo(
    () => [
      { accessorKey: 'material', header: 'Material (SKU ref)' },
      { accessorKey: 'producto', header: 'Producto', Cell: ({ row }) => `${row.original.marca} ${row.original.modelo}`.trim() },
      { accessorKey: 'sku_name', header: 'Color / versión' },
      { accessorKey: 'vtex_sku_id', header: 'SKU Vtex' },
      {
        accessorKey: 'emparejado',
        header: 'En catálogo',
        Cell: ({ cell }) =>
          String(cell.getValue()) === '1' ? <Chip size="small" color="success" label="Sí" variant="outlined" /> : <Chip size="small" label="No" variant="outlined" />,
      },
    ],
    [],
  )

  const colsPrev = useMemo(
    () => [
      { accessorKey: 'material', header: 'Material' },
      { accessorKey: 'modelo', header: 'Modelo' },
      { accessorKey: 'vtex_sku_id', header: 'SKU Vtex destino' },
      { accessorKey: 'pvp_actual', header: 'Precio que se enviaría', Cell: ({ cell }) => fmt(cell.getValue()) },
    ],
    [],
  )

  const colsEstado = useMemo(
    () => [
      { accessorKey: 'material', header: 'Material' },
      { accessorKey: 'producto', header: 'Producto', Cell: ({ row }) => `${row.original.marca} ${row.original.modelo}`.trim() },
      { accessorKey: 'vtex_sku_id', header: 'SKU Vtex' },
      { accessorKey: 'estado', header: 'Estado en Vtex', Cell: ({ cell }) => chipFicha(cell.getValue()) },
      { accessorKey: 'actualizado', header: 'Actualizado' },
    ],
    [],
  )

  return (
    <div>
      <h3 style={{ fontSize: 15, marginBottom: 2 }}>Ecommerce · Conexión Vtex</h3>
      <p style={{ fontSize: 12.5, color: '#6b7684', marginTop: 0 }}>
        Vincula cada material (SKU) con su producto en Vtex y prueba el envío de precios — sin afectar Vtex real.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <Button variant="contained" component="label">
          Importar Excel de Vtex
          <input type="file" accept=".xlsx,.xls" hidden onChange={onArchivo} />
        </Button>
        <Chip label={`${mapeos.length} SKU mapeados · ${emparejados} en catálogo`} />
        <Button variant="outlined" onClick={simularEnvio} disabled={!preview.length}>
          Simular envío a Vtex ({preview.length} pendientes)
        </Button>
      </div>

      {preview.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <h4 style={{ fontSize: 13, margin: '0 0 6px' }}>Qué se enviaría a Vtex ahora (precios pendientes de reflejar)</h4>
          <TablaPrecios columns={colsPrev} data={preview} pageSize={10} />
        </div>
      )}

      <h4 style={{ fontSize: 13, margin: '10px 0 6px' }}>Estado de fichas en Vtex</h4>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <Chip size="small" color="success" variant="outlined" label={`Activos: ${cuenta('activo')}`} />
        <Chip size="small" color="warning" variant="outlined" label={`Inactivos: ${cuenta('inactivo')}`} />
        <Chip size="small" color="error" variant="outlined" label={`Sin ficha: ${cuenta('sin_ficha')}`} />
        <Chip size="small" variant="outlined" label={`Huérfanos: ${cuenta('huerfano')}`} />
      </div>
      <TablaPrecios columns={colsEstado} data={estado} isLoading={isLoading} pageSize={15} />

      <h4 style={{ fontSize: 13, margin: '18px 0 6px' }}>Mapeo material ↔ Vtex (solo Victory y TVS)</h4>
      <TablaPrecios columns={cols} data={mapeos} isLoading={isLoading} pageSize={15} />

      <Paper variant="outlined" style={{ padding: 16, borderRadius: 10, marginTop: 18 }}>
        <h4 style={{ fontSize: 13, margin: '0 0 6px' }}>Endpoint para VTEX</h4>
        <p style={{ fontSize: 12.5, color: '#6b7684', marginTop: 0 }}>
          Bonos vigentes de un producto, por material o por SKU de Vtex. VTEX lo consume desde el servicio de leads:
        </p>
        <div style={{ fontSize: 12.5, fontFamily: 'monospace', userSelect: 'all', marginBottom: 6 }}>
          https://leads.appengine.lucia.com.co/runt/precios/api/bonos/?material=&lt;material&gt;
        </div>
        <div style={{ fontSize: 12.5, fontFamily: 'monospace', userSelect: 'all', marginBottom: 6 }}>
          https://leads.appengine.lucia.com.co/runt/precios/api/bonos/?sku=&lt;vtexSkuId&gt;
        </div>
        <p style={{ fontSize: 12, color: '#6b7684', marginTop: 0 }}>
          Requiere <code>apikey</code> en la URL o en el encabezado. La llave se entrega aparte. «Probar» consulta
          lo mismo con tu sesión de Lucia (sin la llave).
        </p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <TextField size="small" label="Material" value={apiMaterial} onChange={(e) => setApiMaterial(e.target.value)} />
          <TextField size="small" label="SKU de VTEX" value={apiSku} onChange={(e) => setApiSku(e.target.value)} />
          <Button variant="outlined" onClick={probarApi}>Probar</Button>
        </div>

        {apiError && <Alert severity="error">{apiError}</Alert>}
        {apiRespuesta && (
          <pre style={{ fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 320, margin: 0 }}>
            {apiRespuesta}
          </pre>
        )}
      </Paper>
    </div>
  )
}

export default VtexTab
